import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, EmptyState, Spinner, useToast } from '@adop/ui';
import { AlertTriangle, CheckCircle2, Circle, ExternalLink, Eye, Printer as PrinterIcon, Store } from 'lucide-react';
import { formatCents, localParts, zonedToUtc } from '@adop/shared';
import type { Location, PreviewLinkResponse } from '@adop/shared';
import { useWorkspace } from '../../lib/workspace-context';
import { previewUrlFor, storefrontUrlFor } from '../../lib/storefront';
import { useAsync } from '../../lib/useAsync';
import { postJson, errorMessage } from '../../lib/api';
import {
  countOrdersToday,
  fetchMenu,
  listOpeningHours,
  listPrinterDevices,
  monthlyOrderCount,
} from '../../lib/data';
import { computePlanUsage } from '../../lib/plan-usage';
import { computePublishChecklist, isReadyToPublish, type ChecklistItem } from '../../lib/checklist';
import { isPrinterOnline, relativeTime } from '../../lib/format';
import { Alert } from '../../components/Alert';
import { PageHeader } from '../../components/PageHeader';

interface HomeData {
  hoursCount: number;
  itemCount: number;
  devices: Awaited<ReturnType<typeof listPrinterDevices>>;
  orderCount: number;
  revenueCents: number;
  usedThisMonth: number;
}

function todayStartIso(location: Location): string {
  const date = localParts(new Date(), location.timezone).date;
  return zonedToUtc(date, '00:00:00', location.timezone).toISOString();
}

/** Where each checklist item is completed. */
const CHECKLIST_ROUTES: Record<ChecklistItem['key'], string> = {
  branding: '/dashboard/branding',
  hours: '/dashboard/hours',
  menu: '/dashboard/menu',
  payments: '/dashboard/payments',
  printer: '/dashboard/printers',
};

export function HomePage() {
  const { location, restaurant, canManage } = useWorkspace();
  const { toast } = useToast();
  const [previewing, setPreviewing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const state = useAsync<HomeData>(async () => {
    if (!location) throw new Error('No location selected.');
    const [hours, menu, devices, today, monthly] = await Promise.all([
      listOpeningHours(location.id),
      fetchMenu(location.id),
      listPrinterDevices(location.id),
      countOrdersToday(location.id, todayStartIso(location)),
      restaurant ? monthlyOrderCount(restaurant.id) : Promise.resolve(0),
    ]);
    return {
      hoursCount: hours.length,
      itemCount: menu.items.length,
      devices,
      orderCount: today.count,
      revenueCents: today.revenueCents,
      usedThisMonth: monthly,
    };
  }, [location?.id]);

  if (!location) {
    return (
      <EmptyState
        icon={<Store className="h-6 w-6" />}
        title="No location selected"
        description="Add a location to get started."
      />
    );
  }

  const siteUrl = storefrontUrlFor(location.slug);

  const openPreview = async () => {
    setPreviewing(true);
    setActionError(null);
    try {
      const response = await postJson<PreviewLinkResponse>(
        `/api/platform/locations/${location.id}/preview-link`,
      );
      window.open(previewUrlFor(location.slug, response.url), '_blank', 'noopener,noreferrer');
    } catch (cause) {
      const message = errorMessage(cause);
      setActionError(message);
      toast({ title: 'Could not open preview', description: message, variant: 'error' });
    } finally {
      setPreviewing(false);
    }
  };

  const branding = location.branding as { logo_key?: string | null; primary_color?: string } | null;
  const hasBranding = Boolean(branding?.logo_key) || Boolean(branding?.primary_color);
  const devices = state.data?.devices ?? [];
  const activeDevices = devices.filter((device) => !device.revoked_at);
  const checklist = computePublishChecklist({
    hasBranding,
    hasHours: (state.data?.hoursCount ?? 0) > 0,
    itemCount: state.data?.itemCount ?? 0,
    payInStoreEnabled: location.pay_in_store_enabled,
    stripeChargesEnabled: restaurant?.stripe_charges_enabled ?? false,
    reservationsEnabled: location.reservations_enabled,
    printerCount: activeDevices.length,
  });
  const ready = isReadyToPublish(checklist, location.ordering_enabled);
  const nextItem = checklist.find((item) => !item.done) ?? null;
  const primaryDevice = activeDevices[0] ?? null;
  const online = primaryDevice ? isPrinterOnline(primaryDevice.last_seen_at) : false;
  const planUsage = computePlanUsage(restaurant?.plan ?? null, state.data?.usedThisMonth ?? 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title={location.name}
        description={
          <span className="block">
            <span className="block">
              {location.published ? 'Your storefront is live.' : 'Your storefront is not published yet.'}
            </span>
            <span className="mt-0.5 block text-xs text-zinc-400">{siteUrl}</span>
          </span>
        }
        action={
          <div className="flex flex-wrap items-center gap-2">
            {location.published ? (
              <Badge variant="success">Published</Badge>
            ) : (
              <Badge variant="warning">Unpublished</Badge>
            )}
            {location.published ? (
              <Button
                size="sm"
                onClick={() => window.open(siteUrl, '_blank', 'noopener,noreferrer')}
              >
                <ExternalLink className="h-4 w-4" /> View live site
              </Button>
            ) : (
              <Button size="sm" loading={previewing} onClick={() => void openPreview()}>
                <Eye className="h-4 w-4" /> Preview site
              </Button>
            )}
          </div>
        }
      />

      {state.error ? <Alert>{state.error}</Alert> : null}
      {actionError ? <Alert>{actionError}</Alert> : null}

      {planUsage.over && planUsage.includedOrdersPerMonth !== null ? (
        <Alert>
          You&apos;ve used {planUsage.used} of {planUsage.includedOrdersPerMonth} included orders this month
          —{' '}
          <Link className="font-medium underline" to="/dashboard/billing">
            upgrade your plan
          </Link>
          .
        </Alert>
      ) : null}

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent>
            <p className="text-sm text-zinc-500">Orders today</p>
            <p className="mt-1 text-3xl font-bold text-zinc-900">
              {state.loading ? <Spinner /> : state.data?.orderCount ?? 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <p className="text-sm text-zinc-500">Revenue today</p>
            <p className="mt-1 text-3xl font-bold text-zinc-900">
              {state.loading ? <Spinner /> : formatCents(state.data?.revenueCents ?? 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <p className="text-sm text-zinc-500">Printer</p>
            <div className="mt-1 flex items-center gap-2">
              <span
                className={`h-2.5 w-2.5 rounded-full ${online ? 'bg-emerald-500' : 'bg-zinc-300'}`}
                aria-hidden="true"
              />
              <span className="text-lg font-semibold text-zinc-900">{online ? 'Online' : 'Offline'}</span>
            </div>
            <p className="mt-1 text-xs text-zinc-500">
              {primaryDevice
                ? primaryDevice.last_seen_at
                  ? `Last seen ${relativeTime(primaryDevice.last_seen_at)}`
                  : 'Never seen'
                : 'No printer paired'}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle>Plan usage</CardTitle>
            <Badge variant={planUsage.known ? 'outline' : 'warning'}>{planUsage.label}</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {planUsage.known ? (
            <>
              <p className="text-sm text-zinc-600">
                {planUsage.includedOrdersPerMonth === null
                  ? `${planUsage.used} orders this month · Unlimited included`
                  : `${planUsage.used} of ${planUsage.includedOrdersPerMonth} included orders this month`}
              </p>
              {planUsage.percentUsed !== null ? (
                <div className="h-2 w-full overflow-hidden rounded-full bg-zinc-100">
                  <div
                    className={`h-full ${planUsage.over ? 'bg-red-500' : 'bg-emerald-500'}`}
                    style={{ width: `${planUsage.percentUsed}%` }}
                  />
                </div>
              ) : null}
            </>
          ) : planUsage.plan === null ? (
            <p className="text-sm text-zinc-600">No plan is set on this restaurant.</p>
          ) : (
            <p className="text-sm text-zinc-600">
              Your restaurant is on the “{planUsage.plan}” plan. We don&apos;t have an order allowance for
              that plan name, so there is nothing to meter here.
            </p>
          )}
          {planUsage.over && planUsage.includedOrdersPerMonth !== null ? (
            <p className="text-sm font-medium text-red-700">
              You&apos;re over your included volume.
            </p>
          ) : null}
          <Link
            to="/dashboard/billing"
            className="inline-block text-sm font-medium text-zinc-900 underline"
          >
            {planUsage.over ? 'Upgrade plan' : 'Manage plan'} →
          </Link>
          <p className="text-xs text-zinc-500">
            A usage meter only. We never block orders or bill you automatically.
          </p>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Ready to publish</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {checklist.map((item) => (
                <li key={item.key} className="flex items-start gap-3">
                  {item.done ? (
                    <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-500" />
                  ) : (
                    <Circle className="mt-0.5 h-5 w-5 text-zinc-300" />
                  )}
                  <div>
                    <Link
                      to={CHECKLIST_ROUTES[item.key]}
                      className={`text-sm font-medium hover:underline ${
                        item.done ? 'text-zinc-500 line-through' : 'text-zinc-900'
                      }`}
                    >
                      {item.label}
                    </Link>
                    {!item.done ? <p className="text-xs text-zinc-500">{item.detail}</p> : null}
                  </div>
                </li>
              ))}
            </ul>
            {!ready ? (
              <div className="mt-4">
                <Alert variant="info">
                  Complete the required items (hours, menu and payments) before publishing.
                </Alert>
              </div>
            ) : null}
            <div className="mt-4">
              {ready ? (
                <Link to="/dashboard/branding" className="text-sm font-medium text-zinc-900 underline">
                  Publish your site →
                </Link>
              ) : nextItem ? (
                <Link
                  to={CHECKLIST_ROUTES[nextItem.key]}
                  className="text-sm font-medium text-zinc-900 underline"
                >
                  Continue setup: {nextItem.label} →
                </Link>
              ) : null}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick links</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              {[
                { to: '/dashboard/orders', label: 'Live orders' },
                { to: '/dashboard/menu', label: 'Edit menu' },
                { to: '/dashboard/hours', label: 'Opening hours' },
                { to: '/dashboard/printers', label: 'Printers' },
                ...(canManage ? [{ to: '/dashboard/team', label: 'Invite staff' }] : []),
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="rounded-lg border border-zinc-200 px-3 py-2 text-sm font-medium text-zinc-700 hover:border-zinc-300 hover:bg-zinc-50"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {primaryDevice && !online ? (
        <Card>
          <CardContent>
            <div className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-sm">
                {primaryDevice.name} is offline. Orders will queue and print when it reconnects.
              </span>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {activeDevices.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-6 text-center">
            <PrinterIcon className="h-8 w-8 text-zinc-400" />
            <p className="text-sm text-zinc-500">
              No printer paired yet. Pair one so tickets print automatically.
            </p>
            <Link to="/dashboard/printers" className="text-sm font-medium text-zinc-900 underline">
              Pair a printer →
            </Link>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
